import React from 'react';
import {
  FaHome,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaLayerGroup,
  FaHourglassHalf,
  FaTasks,
  FaStickyNote,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { formatTimestamp, formatAllocationType } from '../services/jobService';
import { terminology } from '../config/terminology';

const PendingQuotationList = ({
  schedules,
  loading,
  error,
  initialLoaded,
  pricePerHourInputs,
  notesForAdminInputs,
  frequencyInputs,
  isSubmittingQuote,
  quoteSubmitError,
  nextPageDocId,
  onPricePerHourChange,
  onNotesForAdminChange,
  onFrequencyChange,
  onQuoteSubmit,
  onLoadMore,
  isReadOnly
}) => {
  if (loading && schedules.length === 0) {
    return <p className="text-center text-gray-500 py-5">Loading schedules pending your quotation...</p>;
  }

  if (error) {
    return <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error: {error}</p>;
  }

  if (!loading && !error && schedules.length === 0 && initialLoaded) {
    return <p className="text-center text-gray-500 py-5">No jobs found pending your quotation.</p>;
  }

  if (schedules.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {schedules.map((schedule) => {
        const isFixedFee = schedule.allocationType === 'fixed-fee';
        let allottedHoursLabel = 'Allotted';
        if (schedule.allocationType === 'monthly') {
          allottedHoursLabel = 'Allocation per month';
        } else if (schedule.allocationType === 'per-visit') {
          allottedHoursLabel = 'Allocation per visit';
        }

        return (
          <div key={schedule.id} className="bg-gray-50 shadow-md rounded-lg p-4 border border-gray-200">
            {/* Row 1: Client and Household Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b">
              <div>
                <p className="font-semibold mb-1">
                  <FaHome className="inline mr-1 text-gray-500"/>
                  {terminology.household.singular}
                </p>
                <p>{`${schedule.householdInfo?.firstName || ''} ${schedule.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
                <p className="text-xs text-gray-500">
                  <FaMapMarkerAlt className="inline mr-1"/>
                  {`${schedule.householdInfo?.addressLine1 || ''}${schedule.householdInfo?.state ? ', ' + schedule.householdInfo.state : ''} ${schedule.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address'}
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">
                  <FaUser className="inline mr-1 text-gray-500"/>
                  {terminology.client.singular}
                </p>
                <p>{schedule.clientInfo?.clientName || 'N/A'}</p>
              </div>
            </div>

            {/* Row 2: Schedule Details and Time */}
            <div className="text-sm text-gray-700 space-y-1 mb-3">
              <p className="font-semibold capitalize">
                <FaCalendarAlt className="inline mr-2 text-jims-cyan" />
                {formatTimestamp(schedule.startDate)}
                {schedule.jobTime && (
                  <span className="ml-3">
                    <FaClock className="inline mr-1 text-jims-cyan" />
                    Preferred Time: {schedule.jobTime}
                  </span>
                )}
              </p>
            </div>

            {/* Row 3: Allocation, Hours, Services, and Notes */}
            <div className="text-sm text-gray-700 space-y-1 mb-3">
              <p>
                {schedule.allocationType && (
                  <span className="mr-3 inline-flex items-center">
                    <FaLayerGroup className="mr-1 text-jims-cyan" />
                    Allocation: {formatAllocationType(schedule.allocationType)}
                  </span>
                )}
                {/* Hours are hidden on a set fee: there allotedHours is not an allocation but the invoice
                    quantity (quote / supplier hourly rate), so it gives away the supplier's rate by division. */}
                {!isFixedFee && schedule.allotedHours != null && (
                  <span className="mr-3 inline-flex items-center">
                    <FaHourglassHalf className="mr-1 text-jims-cyan" />
                    {allottedHoursLabel}: {schedule.allotedHours} hr(s)
                  </span>
                )}
              </p>
              {schedule.services && schedule.services.length > 0 && (
                <p>
                  <FaTasks className="inline mr-1 text-jims-cyan" />
                  Services: {schedule.services.map(s => s.name).join(', ')}
                </p>
              )}
              {schedule.notesForFranchisee && (
                <p>
                  <FaStickyNote className="inline mr-1 text-jims-cyan" />
                  Notes: <span className="text-gray-600 italic">{schedule.notesForFranchisee}</span>
                </p>
              )}
            </div>

            {/* Row 4: Price Per Hour Input and Submit Button */}
            <div className="flex flex-col md:flex-row items-end space-y-2 md:space-y-0 md:space-x-4 mt-4 pt-3 border-t border-gray-200">
              <div className="flex-grow max-w-xs">
                <label htmlFor={`pricePerHour-${schedule.id}`} className="block text-sm font-medium text-gray-700 mb-1">
                  {schedule.allocationType === 'fixed-fee' ? 'Total Price Quote ($ Incl. GST)' : 'Price Quote Per Hour ($ Incl. GST)'}
                </label>
                <input
                  type="number"
                  id={`pricePerHour-${schedule.id}`}
                  value={pricePerHourInputs[schedule.id] || ''}
                  onChange={(e) => onPricePerHourChange(schedule.id, e.target.value)}
                  placeholder="Enter price"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100"
                  min="0"
                  step="0.01"
                  disabled={isReadOnly}
                />
              </div>
              {!isReadOnly && (
              <button
                onClick={() => onQuoteSubmit(schedule.id, parseFloat(pricePerHourInputs[schedule.id]))}
                disabled={!pricePerHourInputs[schedule.id] || parseFloat(pricePerHourInputs[schedule.id]) <= 0 || isSubmittingQuote[schedule.id]}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-4 md:mt-0 md:self-end"
              >
                {isSubmittingQuote[schedule.id] ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Quote'
                )}
              </button>
              )}
            </div>

            {/* Notes for Admin Section */}
            <div className="mt-2 md:mt-4 w-full md:w-3/4">
              <label htmlFor={`notesForAdmin-${schedule.id}`} className="block text-sm font-medium text-gray-700 mb-1">
                Notes for Admin (e.g. tasks included, recommended frequency, etc.)
              </label>
              <textarea
                id={`notesForAdmin-${schedule.id}`}
                value={notesForAdminInputs[schedule.id] || ''}
                onChange={(e) => onNotesForAdminChange(schedule.id, e.target.value)}
                placeholder="Enter notes for admin (optional)"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-y min-h-[60px] disabled:bg-gray-100"
                rows={2}
                disabled={isReadOnly}
              />
            </div>

            {/* Frequency Selection for Recurring Schedules */}
            {schedule.scheduleType === 'recurring' && (
              <div className="mt-4 w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Frequency (Optional)
                </label>
                <div className="flex items-center gap-6 mb-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`frequencyOption-${schedule.id}`}
                      value="interval"
                      checked={frequencyInputs[schedule.id]?.option === 'interval'}
                      onChange={(e) => onFrequencyChange(schedule.id, 'option', e.target.value)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isReadOnly}
                    />
                    <span className="ml-2 text-sm text-gray-700">Every</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={frequencyInputs[schedule.id]?.intervalValue || ''}
                      onChange={(e) => onFrequencyChange(schedule.id, 'intervalValue', e.target.value)}
                      disabled={isReadOnly || frequencyInputs[schedule.id]?.option !== 'interval'}
                      className={`w-16 ml-2 px-2 py-1 text-sm border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        isReadOnly || frequencyInputs[schedule.id]?.option !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                      }`}
                      placeholder="1"
                    />
                    <select
                      value={frequencyInputs[schedule.id]?.intervalUnit || 'days'}
                      onChange={(e) => onFrequencyChange(schedule.id, 'intervalUnit', e.target.value)}
                      disabled={isReadOnly || frequencyInputs[schedule.id]?.option !== 'interval'}
                      className={`ml-2 px-2 py-1 text-sm border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        isReadOnly || frequencyInputs[schedule.id]?.option !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                    </select>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`frequencyOption-${schedule.id}`}
                      value="monthly"
                      checked={frequencyInputs[schedule.id]?.option === 'monthly'}
                      onChange={(e) => onFrequencyChange(schedule.id, 'option', e.target.value)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isReadOnly}
                    />
                    <span className="ml-2 text-sm text-gray-700">Monthly</span>
                  </label>
                </div>
                {/* Show frequency validation hint */}
                {frequencyInputs[schedule.id]?.option === 'interval' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a whole number (e.g., 1, 2, 3...) for the frequency
                  </p>
                )}
              </div>
            )}

            {/* Error Display */}
            {quoteSubmitError[schedule.id] && (
              <p className="text-xs text-red-600 mt-1 ml-1">{quoteSubmitError[schedule.id]}</p>
            )}

            {/* Description is hidden: it is auto-generated from the supplier hourly rate, which franchisees must not see. */}
          </div>
        );
      })}

      {/* Load More Button */}
      {nextPageDocId && (
        <div className="mt-6 text-center">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More Pending Your Quotation'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PendingQuotationList;
